<template>
    <div class="webrtc-container">
      <div class="header">
        <h2>WebRTC 视频流传输 Demo</h2>
        <div class="status-info">
          <span :class="['status-dot', connectionStatus]"></span>
          <span>{{ statusText }}</span>
        </div>
      </div>

      <div class="video-section">
        <!-- 本地视频（发送端） -->
        <div class="video-box">
          <h3>本地视频（发送端）</h3>
          <video
            ref="localVideo"
            autoplay
            playsinline
            muted
            class="video-element"
          ></video>
          <div class="video-controls">
            <van-button
              type="primary"
              size="small"
              @click="startCapture"
              :disabled="isCapturing"
            >
              开始采集
            </van-button>
            <van-button
              type="danger"
              size="small"
              @click="stopCapture"
              :disabled="!isCapturing"
            >
              停止采集
            </van-button>
            <van-button
              type="info"
              size="small"
              @click="connectWebRTC"
              :disabled="!isCapturing || isConnected"
            >
              连接服务器
            </van-button>
          </div>
        </div>

        <!-- 远程视频（接收端预览） -->
        <div class="video-box">
          <h3>远程视频（接收端预览）</h3>
          <video
            ref="remoteVideo"
            autoplay
            playsinline
            class="video-element"
          ></video>
          <div class="video-info">
            <p v-if="!remoteStream">等待接收视频流...</p>
            <p v-else>已接收视频流</p>
          </div>
        </div>
      </div>

      <!-- 配置信息 -->
      <div class="config-section">
        <h3>配置信息</h3>
        <div class="config-item">
          <label>信令服务器地址：</label>
          <van-field
            v-model="wsUrl"
            placeholder="ws://localhost:8080/webrtc-signal"
            size="small"
          />
        </div>
        <div class="config-item">
          <label>STUN服务器：</label>
          <van-field
            v-model="stunServer"
            placeholder="stun:stun.l.google.com:19302"
            size="small"
          />
        </div>
        <div class="config-item">
          <label>客户端类型：</label>
          <van-radio-group v-model="clientType" direction="horizontal">
            <van-radio name="sender">发送端</van-radio>
            <van-radio name="receiver">接收端</van-radio>
          </van-radio-group>
        </div>
      </div>

      <!-- 日志信息 -->
      <div class="log-section">
        <h3>日志信息</h3>
        <div class="log-content" ref="logContent">
          <div
            v-for="(log, index) in logs"
            :key="index"
            :class="['log-item', log.type]"
          >
            <span class="log-time">{{ log.time }}</span>
            <span class="log-message">{{ log.message }}</span>
          </div>
        </div>
        <van-button size="small" @click="clearLogs">清空日志</van-button>
      </div>
    </div>
  </template>

<script>
import { Toast } from 'vant';

export default {
  name: 'WebRTCDemo',
  data() {
    return {
      // 视频流
      localStream: null,
      remoteStream: null,

      // WebRTC连接
      peerConnection: null,

      // WebSocket连接（信令服务器）
      ws: null,

      // 状态
      isCapturing: false,
      isConnected: false,
      connectionStatus: 'disconnected', // disconnected, connecting, connected

      // 配置
      wsUrl: 'ws://localhost:8080/webrtc-signal',
      stunServer: 'stun:stun.l.google.com:19302',
      clientType: 'sender', // sender or receiver

      // 日志
      logs: [],
    };
  },
  computed: {
    statusText() {
      const statusMap = {
        disconnected: '未连接',
        connecting: '连接中...',
        connected: '已连接',
      };
      return statusMap[this.connectionStatus] || '未知状态';
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    /**
       * 添加日志
       */
    addLog(message, type = 'info') {
      const time = new Date().toLocaleTimeString();
      this.logs.push({ time, message, type });
      // 自动滚动到底部
      this.$nextTick(() => {
        if (this.$refs.logContent) {
          this.$refs.logContent.scrollTop = this.$refs.logContent.scrollHeight;
        }
      });
      // console.log(`[${type.toUpperCase()}] ${message}`);
    },

    /**
       * 清空日志
       */
    clearLogs() {
      this.logs = [];
    },

    /**
       * 开始采集视频
       */
    async startCapture() {
      try {
        this.addLog('开始请求摄像头权限...', 'info');

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 30 },
          },
          audio: true, // 可以根据需要开启音频
        });

        this.localStream = stream;
        if (this.$refs.localVideo) {
          this.$refs.localVideo.srcObject = stream;
        }

        this.isCapturing = true;
        this.addLog('摄像头采集成功', 'success');
        Toast.success('摄像头采集成功');
      } catch (error) {
        this.addLog(`摄像头采集失败: ${error.message}`, 'error');
        Toast.fail('摄像头采集失败');
        console.error('Error accessing camera:', error);
      }
    },

    /**
       * 停止采集视频
       */
    stopCapture() {
      if (this.localStream) {
        this.localStream.getTracks().forEach((track) => track.stop());
        this.localStream = null;
      }

      if (this.$refs.localVideo) {
        this.$refs.localVideo.srcObject = null;
      }

      this.isCapturing = false;
      this.addLog('已停止摄像头采集', 'info');

      // 如果已连接，断开连接
      if (this.isConnected) {
        this.disconnectWebRTC();
      }
    },

    /**
       * 创建RTCPeerConnection
       */
    createPeerConnection() {
      const configuration = {
        iceServers: [
          { urls: this.stunServer },
          // 如果需要TURN服务器，可以添加：
          // {
          //   urls: 'turn:your-turn-server.com:3478',
          //   username: 'username',
          //   credential: 'password'
          // }
        ],
      };

      this.peerConnection = new RTCPeerConnection(configuration);

      // 添加本地流到peerConnection
      if (this.localStream && this.clientType === 'sender') {
        this.localStream.getTracks().forEach((track) => {
          this.peerConnection.addTrack(track, this.localStream);
          this.addLog(`已添加轨道: ${track.kind}`, 'info');
        });
      }

      // 监听ICE候选
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.addLog('发现ICE候选', 'info');
          this.sendSignal({
            type: 'ice-candidate',
            candidate: event.candidate,
          });
        }
      };

      // 监听连接状态变化
      this.peerConnection.onconnectionstatechange = () => {
        this.addLog(`连接状态: ${this.peerConnection.connectionState}`, 'info');
        if (this.peerConnection.connectionState === 'connected') {
          this.connectionStatus = 'connected';
          this.isConnected = true;
          Toast.success('WebRTC连接成功');
        } else if (this.peerConnection.connectionState === 'disconnected'
                     || this.peerConnection.connectionState === 'failed') {
          this.connectionStatus = 'disconnected';
          this.isConnected = false;
          Toast.fail('WebRTC连接断开');
        }
      };

      // 接收远程流（接收端）
      if (this.clientType === 'receiver') {
        this.peerConnection.ontrack = (event) => {
          this.addLog('收到远程视频流', 'success');
          this.remoteStream = event.streams[0];
          if (this.$refs.remoteVideo) {
            this.$refs.remoteVideo.srcObject = this.remoteStream;
          }
        };
      }

      this.addLog('RTCPeerConnection已创建', 'success');
    },

    /**
       * 连接WebRTC服务器
       */
    async connectWebRTC() {
      if (!this.localStream && this.clientType === 'sender') {
        Toast.fail('请先开始采集视频');
        return;
      }

      try {
        this.connectionStatus = 'connecting';
        this.addLog('正在连接信令服务器...', 'info');

        // 连接WebSocket
        this.connectWebSocket();

        // 创建PeerConnection
        this.createPeerConnection();

        // 如果是发送端，创建offer
        if (this.clientType === 'sender') {
          await this.createOffer();
        }
      } catch (error) {
        this.addLog(`连接失败: ${error.message}`, 'error');
        Toast.fail('连接失败');
        console.error('Error connecting:', error);
      }
    },

    /**
       * 连接WebSocket信令服务器
       */
    connectWebSocket() {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        this.addLog('WebSocket连接成功', 'success');
        // 注册客户端类型
        this.sendSignal({
          type: 'register',
          clientType: this.clientType,
        });
      };

      this.ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          await this.handleSignal(message);
        } catch (error) {
          this.addLog(`处理信令消息失败: ${error.message}`, 'error');
        }
      };

      this.ws.onerror = (error) => {
        this.addLog('WebSocket错误', 'error');
        console.error('WebSocket error:', error);
      };

      this.ws.onclose = () => {
        this.addLog('WebSocket连接关闭', 'warning');
        this.connectionStatus = 'disconnected';
        this.isConnected = false;
      };
    },

    /**
       * 发送信令消息
       */
    sendSignal(message) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
        this.addLog(`发送信令: ${message.type}`, 'info');
      } else {
        this.addLog('WebSocket未连接，无法发送信令', 'error');
      }
    },

    /**
       * 处理信令消息
       */
    async handleSignal(message) {
      this.addLog(`收到信令: ${message.type}`, 'info');

      switch (message.type) {
        case 'offer':
          if (this.clientType === 'receiver') {
            await this.handleOffer(message.offer);
          }
          break;

        case 'answer':
          if (this.clientType === 'sender') {
            await this.handleAnswer(message.answer);
          }
          break;

        case 'ice-candidate':
          await this.handleIceCandidate(message.candidate);
          break;

        case 'registered':
          this.addLog('已注册到服务器', 'success');
          break;

        default:
          this.addLog(`未知信令类型: ${message.type}`, 'warning');
      }
    },

    /**
       * 创建Offer（发送端）
       */
    async createOffer() {
      try {
        const offer = await this.peerConnection.createOffer();
        await this.peerConnection.setLocalDescription(offer);
        this.addLog('已创建Offer', 'success');

        this.sendSignal({
          type: 'offer',
          offer,
        });
      } catch (error) {
        this.addLog(`创建Offer失败: ${error.message}`, 'error');
        throw error;
      }
    },

    /**
       * 处理Offer（接收端）
       */
    async handleOffer(offer) {
      try {
        await this.peerConnection.setRemoteDescription(offer);
        this.addLog('已设置远程Description(Offer)', 'success');

        const answer = await this.peerConnection.createAnswer();
        await this.peerConnection.setLocalDescription(answer);
        this.addLog('已创建Answer', 'success');

        this.sendSignal({
          type: 'answer',
          answer,
        });
      } catch (error) {
        this.addLog(`处理Offer失败: ${error.message}`, 'error');
        throw error;
      }
    },

    /**
       * 处理Answer（发送端）
       */
    async handleAnswer(answer) {
      try {
        await this.peerConnection.setRemoteDescription(answer);
        this.addLog('已设置远程Description(Answer)', 'success');
      } catch (error) {
        this.addLog(`处理Answer失败: ${error.message}`, 'error');
        throw error;
      }
    },

    /**
       * 处理ICE候选
       */
    async handleIceCandidate(candidate) {
      try {
        await this.peerConnection.addIceCandidate(candidate);
        this.addLog('已添加ICE候选', 'info');
      } catch (error) {
        this.addLog(`添加ICE候选失败: ${error.message}`, 'error');
      }
    },

    /**
       * 断开WebRTC连接
       */
    disconnectWebRTC() {
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      if (this.remoteStream) {
        this.remoteStream.getTracks().forEach((track) => track.stop());
        this.remoteStream = null;
      }

      if (this.$refs.remoteVideo) {
        this.$refs.remoteVideo.srcObject = null;
      }

      this.isConnected = false;
      this.connectionStatus = 'disconnected';
      this.addLog('已断开WebRTC连接', 'info');
    },

    /**
       * 清理资源
       */
    cleanup() {
      this.stopCapture();
      this.disconnectWebRTC();
    },
  },
};
</script>

  <style scoped>
  .webrtc-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #eee;
  }

  .header h2 {
    margin: 0;
    color: #333;
  }

  .status-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
  }

  .status-dot.disconnected {
    background-color: #999;
  }

  .status-dot.connecting {
    background-color: #ff9800;
    animation: pulse 1.5s infinite;
  }

  .status-dot.connected {
    background-color: #4caf50;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .video-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .video-box {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    background: #f9f9f9;
  }

  .video-box h3 {
    margin: 0 0 10px 0;
    font-size: 16px;
    color: #666;
  }

  .video-element {
    width: 100%;
    height: 300px;
    background: #000;
    border-radius: 4px;
    object-fit: cover;
  }

  .video-controls {
    margin-top: 10px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .video-info {
    margin-top: 10px;
    text-align: center;
    color: #666;
    font-size: 14px;
  }

  .config-section {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
    background: #f9f9f9;
  }

  .config-section h3 {
    margin: 0 0 15px 0;
    font-size: 16px;
    color: #333;
  }

  .config-item {
    margin-bottom: 15px;
  }

  .config-item label {
    display: block;
    margin-bottom: 5px;
    font-size: 14px;
    color: #666;
  }

  .log-section {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    background: #f9f9f9;
  }

  .log-section h3 {
    margin: 0 0 15px 0;
    font-size: 16px;
    color: #333;
  }

  .log-content {
    height: 200px;
    overflow-y: auto;
    background: #1e1e1e;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 10px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }

  .log-item {
    margin-bottom: 5px;
    line-height: 1.5;
  }

  .log-time {
    color: #888;
    margin-right: 10px;
  }

  .log-item.info .log-message {
    color: #4fc3f7;
  }

  .log-item.success .log-message {
    color: #4caf50;
  }

  .log-item.warning .log-message {
    color: #ff9800;
  }

  .log-item.error .log-message {
    color: #f44336;
  }

  @media (max-width: 768px) {
    .video-section {
      grid-template-columns: 1fr;
    }
  }
  </style>
